export interface SystemNotificationData {
  _id: string
  title: string
  description?: string
  link?: string
}

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  )
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export async function registerPushSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false
  const publicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY
  if (!publicKey) return false
  try {
    const registration = await navigator.serviceWorker.register("/sw.js")
    await navigator.serviceWorker.ready
    let subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
    }
    const res = await fetch("/api/push/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription: subscription.toJSON() }),
    })
    return res.ok
  } catch {
    return false
  }
}

export async function unregisterPushSubscription(): Promise<void> {
  if (!isPushSupported()) return
  let subscription: PushSubscription | null = null
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (registration) {
      subscription = await registration.pushManager.getSubscription()
    }
  } catch {
    /* registro/service worker indisponível */
  }
  const endpoint = subscription?.endpoint
  try {
    if (subscription) await subscription.unsubscribe()
  } catch {
    /* já removida ou falha de rede */
  }
  try {
    await fetch("/api/push/unregister", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(endpoint ? { endpoint } : {}),
    })
  } catch {
    /* falha na chamada ao servidor; o registro é limpo no próximo 410 */
  }
}

export async function ensurePushPermission(): Promise<boolean> {
  if (!isPushSupported()) return false
  if (Notification.permission === "denied") return false
  if (Notification.permission === "default") {
    const result = await Notification.requestPermission()
    if (result !== "granted") return false
  }
  return registerPushSubscription()
}

export async function getBrowserPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) return null
    return await registration.pushManager.getSubscription()
  } catch {
    return null
  }
}

export function promptPushPermission(): void {
  ensurePushPermission().catch(() => {
    /* prompt oportunista em gesto de usuário; falha silenciosa */
  })
}

export function showSystemNotification(data: SystemNotificationData): void {
  if (typeof window === "undefined" || typeof Notification === "undefined") return
  if (Notification.permission !== "granted") return
  try {
    const notification = new Notification(data.title, {
      body: data.description || "",
      icon: "/placeholder-logo.png",
      tag: `pediloo-${data._id}`,
      data: { url: data.link || "/" },
    })
    notification.onclick = () => {
      try {
        window.focus()
        if (data.link) window.location.href = data.link
      } finally {
        notification.close()
      }
    }
    setTimeout(() => notification.close(), 10000)
  } catch {
    /* navegador pode bloquear a criação */
  }
}
